import { lazy, Suspense, useState } from 'react';
import { toast } from 'sonner';
import { FileSignature } from 'lucide-react';
import { ModalShell } from '../ui/ModalShell';
import { Button } from '../ui/Button';
import { SignaturePadModal } from '../kit/SignaturePadModal';
import type { OnboardingDoc, OnboardingDocVersion } from '../../hooks/useOnboardingDocuments';

const PdfViewer = lazy(() => import('../pdf/PdfViewer'));

/**
 * Admin-only dry run of the inductee read-and-sign flow. Mirrors
 * DocumentSignRow (the real /me experience) field for field - PDF, optional
 * choice, read confirmation, signature pad - but signs nothing: the pad just
 * confirms what would happen. Lets HR check the rendered document (including
 * a freshly converted Word upload) and see how a "choice before signing"
 * actually appears before assigning it to anyone.
 */
export function DocumentPreviewModal({
  open,
  onClose,
  doc,
  version,
}: {
  open: boolean;
  onClose: () => void;
  doc: OnboardingDoc;
  version: OnboardingDocVersion;
}) {
  const [read, setRead] = useState(false);
  const [option, setOption] = useState('');
  const [padOpen, setPadOpen] = useState(false);

  const title = doc.title;
  const needsOption = doc.requires_option;
  const optionReady = !needsOption || option.length > 0;
  const readReady = !doc.require_read || read;

  return (
    <ModalShell open={open} onClose={onClose} titleId="doc-preview-title" maxWidth="max-w-2xl">
      <div className="space-y-3">
        <div>
          <h2 id="doc-preview-title" className="text-lg font-semibold text-fg">
            Preview: {title}
          </h2>
          <p className="mt-0.5 text-sm text-fg-muted">
            Exactly what a new starter sees when they open this to sign (version {version.version}).
            Nothing here is saved.
          </p>
        </div>

        <div className="h-96 w-full overflow-y-auto rounded-token-sm border border-line/10 bg-surface-2 p-2">
          <Suspense
            fallback={<p className="p-6 text-center text-sm text-fg-muted">Loading document…</p>}
          >
            <PdfViewer url={`/api/admin/onboarding-documents/versions/${version.id}/pdf`} />
          </Suspense>
        </div>

        {needsOption ? (
          <fieldset className="space-y-1.5">
            <legend className="text-sm font-medium text-fg-muted">Please choose one:</legend>
            {doc.options.length === 0 ? (
              <p className="text-sm text-warning">
                "Require a choice" is on but no options have been saved yet.
              </p>
            ) : (
              doc.options.map((o) => (
                <label key={o.value} className="flex items-center gap-2 text-sm text-fg">
                  <input
                    type="radio"
                    name="doc-preview-opt"
                    value={o.value}
                    checked={option === o.value}
                    onChange={() => setOption(o.value)}
                  />
                  {o.label}
                </label>
              ))
            )}
          </fieldset>
        ) : null}

        {doc.require_read ? (
          <label className="flex items-start gap-2 text-sm text-fg">
            <input
              type="checkbox"
              checked={read}
              onChange={(e) => setRead(e.target.checked)}
              className="mt-0.5 shrink-0"
            />
            <span>{doc.ack_text?.trim() || 'I have read and understood this document.'}</span>
          </label>
        ) : null}

        <div className="flex items-center justify-end gap-2 border-t border-line/10 pt-3">
          <Button size="sm" variant="ghost" onClick={onClose}>
            Close
          </Button>
          <Button
            size="sm"
            variant="primary"
            iconLeft={<FileSignature className="h-4 w-4" />}
            disabled={!readReady || !optionReady}
            onClick={() => setPadOpen(true)}
          >
            Sign
          </Button>
        </div>
      </div>

      <SignaturePadModal
        open={padOpen}
        title={`Sign: ${title}`}
        onClose={() => setPadOpen(false)}
        onConfirm={() => {
          setPadOpen(false);
          toast.message(
            'Preview only - in the real flow this records the signature and stores the signed PDF.',
          );
        }}
      />
    </ModalShell>
  );
}
